<template>
  <div class="dialer">
    <DevModeBanner />
    <!-- Nothing until the verdict lands: the grid would flash "Queue is clear" at a first-timer. -->
    <div v-if="undecided" class="undecided" aria-busy="true"></div>

    <!-- Onboarding needs nothing from the queue, so a failed load waits behind it. -->
    <div v-else-if="error && !showOnboarding" class="statePanel">
      <p class="stateTitle">Couldn’t load the caller queue</p>
      <p class="stateText">{{ error }}</p>
      <button type="button" class="btn secondary" @click="load()">Retry</button>
    </div>

    <!-- First-time setup owns the whole page: no rail, no focus card, one step at a time. -->
    <DialerOnboarding
      v-else-if="showOnboarding"
      :stage="onboarding.stage.value"
      :queue-state="onboarding.queueState.value"
      :leads="onboarding.leads.value"
      :selected-count="onboarding.selectedCount.value"
      :all-selected="onboarding.allSelected.value"
      :added="onboarding.added.value"
      :adding="onboarding.adding.value"
      :add-error="onboarding.addError.value"
      :phone-skipped="onboarding.phoneSkipped.value"
      :callback-number="onboarding.callbackNumber.value"
      :mic-allowed-earlier="onboarding.micAllowedEarlier.value"
      :landing="landing"
      @start="onboarding.start"
      @continue-mic="onboarding.continueFromMic"
      @skip-phone="onboarding.skipPhone"
      @toggle-lead="onboarding.toggleLead"
      @toggle-all="onboarding.toggleAll"
      @add-selected="onAddSelected"
      @skip-select="onboarding.skipSelect"
      @finish="onFinishOnboarding"
    />

    <!-- Adding contacts takes the page, like setup: one focused card, nothing beside it to misread.
         One source today (the rep's recent LeadIQ captures); a source step goes in front of it later. -->
    <CapturedLeadsPanel
      v-else-if="addOpen"
      :leads="captures.leads.value"
      :selected-count="captures.selectedCount.value"
      :all-selected="captures.allSelected.value"
      :has-addable="captures.hasAddable.value"
      :loading="captures.loading.value"
      :load-error="captures.loadError.value"
      :adding="captures.adding.value || landing"
      :add-error="captures.addError.value"
      @toggle-lead="captures.toggleLead"
      @toggle-all="captures.toggleAll"
      @add="onAddCaptures"
      @cancel="closeAddContacts"
      @retry="captures.load()"
    />

    <div v-else class="dialerGrid" :class="{ noRail: hideRail, railCollapsed }">
      <div v-if="!hideRail" class="railWrap">
        <DialerQueueRail
          v-model:sort-mode="sortMode"
          v-model:filter-play-id="filterPlayId"
          v-model:filter-account-id="filterAccountId"
          :sections="sections"
          :selected-id="selectedId"
          :selected-ids="selectedIds"
          :selected-ready-count="selectionRun.length"
          :all-shown-selected="allShownSelected"
          :play-options="playOptions"
          :account-options-fn="searchAccounts"
          :active-filter-count="activeFilterCount"
          :queued-count="entries.length"
          :ready-count="visibleEntries.length"
          :dimmed="sessionActive"
          :session-controls="sessionControlsActive"
          :session-position="session.position.value"
          :session-total="session.total.value"
          :can-add-contacts="canAddContacts"
          :nothing-ready="nothingReady"
          @start-dialing="onStartDialing"
          @add-contacts="openAddContacts"
          @clear-selection="clearSelection"
          @end-session="onEndSession"
          @select="onRailSelect"
          @toggle-check="toggleSelection"
          @toggle-page="togglePageSelection"
          @bulk-exit="openExitModal"
          @bulk-remove="onBulkRemove"
          @toggle-section="toggleSection"
          @clear-filters="clearFilters"
        >
          <template #head-aside>
            <IconButton
              compact
              class="railToggle"
              :aria-expanded="!railCollapsed"
              :aria-label="railCollapsed ? 'Expand rail' : 'Collapse rail'"
              :title="railCollapsed ? 'Expand rail' : 'Collapse rail'"
              @click="railCollapsed = !railCollapsed"
            >
              <AppIcon :name="railCollapsed ? 'chevron_double_right' : 'chevron_double_left'" :size="16" />
            </IconButton>
          </template>
        </DialerQueueRail>
      </div>
      <DialerSessionView v-if="sessionActive" :play-color="playColor" />
      <DialerFocusCard
        v-else
        :entry="selected"
        :loading="!loaded"
        :group-color="playColor(selected?.sales_play_id)"
        :status-pill="telephony.pill.value"
        :prefs-dot="telephony.myPrefsDot.value"
        :settings-open="telephony.settingsOpen.value"
        :can-add-contacts="canAddContacts"
        @call-contact="onCallContact"
        @one-off-call="onOneOffCall"
        @save-notes="saveNotes"
        @open-history="openHistory"
        @open-settings="onOpenSettings"
        @add-contacts="openAddContacts"
      />
    </div>

    <!-- One outlet for the whole dialer surface: the rail's bulk actions fire
         toasts with no session open. -->
    <DialerToasts />

    <!-- Teleported: `.dialer` is a CSS container, which would make it the
         containing block for the modal's fixed overlay. -->
    <Teleport to="body">
      <ExitPlayModal
        v-if="exitModalIds.length"
        :membership-ids="exitModalIds"
        noun="contact"
        @close="exitModalIds = []"
        @exited="onBulkExited"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import AppIcon from '../../components/AppIcon.vue'
import IconButton from '../components/IconButton.vue'
import DialerQueueRail from '../components/dialer/DialerQueueRail.vue'
import ExitPlayModal, { type BulkExitResult } from '../components/ExitPlayModal.vue'
import DialerFocusCard from '../components/dialer/DialerFocusCard.vue'
import DialerSessionView from '../components/dialer/DialerSessionView.vue'
import DialerToasts from '../components/dialer/DialerToasts.vue'
import DialerOnboarding from '../components/dialer/DialerOnboarding.vue'
import CapturedLeadsPanel from '../components/dialer/CapturedLeadsPanel.vue'
import DevModeBanner from '../components/dialer/DevModeBanner.vue'
import { useToast } from '../composables/useToast'
import { useDialerQueue } from '../composables/useDialerQueue'
import { useDialerOnboarding, type QueueState, type QueuedCompany } from '../composables/useDialerOnboarding'
import { tallyOutcomes, useCapturedLeads } from '../composables/useCapturedLeads'
import { useDialerSession } from '../composables/useDialerSession'
import { firstNameOf } from '../utils/names'
import { useCurrentUser } from '../../services/user'
import { closeAmdStream, openAmdStream } from '../../composables/useDialerAmdStream'
import { useTelephony, type TelephonySurface } from '../composables/useTelephony'
import { useChatScope } from '../composables/useChatScope'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_ACCOUNT, DRAWER_CONTEXT_CONTACT } from '../../drawer/drawerTypes'
import { DIALER_SETTINGS_CONTEXT_TYPE } from '../composables/dialerSettingsContext'
import { preloadCrmLinkConfig } from '../../services/crmLinks'
import { removeFromDialList, type CallerQueueEntry, type QueuedNumber } from '../../services/dialerApi'
import { canAutoDial, canOneOffDial } from '../components/dialer/dialability'
import { linkedContactIds } from '../utils/dialLink'

const {
  entries,
  loading,
  loaded,
  error,
  selectedId,
  selected,
  sections,
  sortMode,
  filterPlayId,
  filterAccountId,
  activeFilterCount,
  rowsFiltered,
  playOptions,
  visibleEntries,
  railOrder,
  playColor,
  select,
  load,
  searchAccounts,
  clearFilters,
  toggleSection,
  saveNotes,
} = useDialerQueue()

const router = useRouter()
const route = useRoute()
const { show: showToast } = useToast()

// Dialer-only reps open on their own dials; their admins, like full users, on everyone's.
function openHistory(): void {
  void router.push(
    isDialerOnly.value && !isAdmin.value
      ? { path: '/dialer/history', query: { rep: me.value?.user_id } }
      : '/dialer/history',
  )
}

// ── Bulk selection (checkboxes) — same model as the Actions page ──
// Keyed by contact: a row's id is its best-dialable line, which changes as numbers rest.
const selectedIds = ref<Set<string>>(new Set())
const lastClickedId = ref<string | null>(null)

/** Contact ids in render order, honoring collapsed sections — what shift-range walks. */
const shownIds = computed(() => {
  const ids: string[] = []
  for (const s of sections.value) {
    if (!s.open) continue
    for (const g of s.groups) for (const e of g.entries) ids.push(e.contact_id)
  }
  return ids
})
const allShownSelected = computed(
  () => shownIds.value.length > 0 && shownIds.value.every((id) => selectedIds.value.has(id)),
)

function toggleSelection(id: string, event: MouseEvent): void {
  const ids = shownIds.value
  if (event.shiftKey && lastClickedId.value !== null) {
    const from = ids.indexOf(lastClickedId.value)
    const to = ids.indexOf(id)
    if (from !== -1 && to !== -1) {
      const [lo, hi] = from < to ? [from, to] : [to, from]
      for (let i = lo; i <= hi; i++) selectedIds.value.add(ids[i])
      lastClickedId.value = id
      return
    }
  }
  if (selectedIds.value.has(id)) selectedIds.value.delete(id)
  else selectedIds.value.add(id)
  lastClickedId.value = id
}

function togglePageSelection(): void {
  if (allShownSelected.value) clearSelection()
  else for (const id of shownIds.value) selectedIds.value.add(id)
}

function clearSelection(): void {
  selectedIds.value.clear()
  lastClickedId.value = null
}

watch(entries, (list) => {
  if (!selectedIds.value.size) return
  const live = new Set(list.map((e) => e.contact_id))
  for (const id of [...selectedIds.value]) if (!live.has(id)) selectedIds.value.delete(id)
})

const exitModalIds = ref<string[]>([])
function openExitModal(): void {
  const ids = new Set<string>()
  for (const e of entries.value) if (selectedIds.value.has(e.contact_id) && e.membership_id) ids.add(e.membership_id)
  exitModalIds.value = [...ids]
}
const contacts = (n: number) => `${n} ${n === 1 ? 'contact' : 'contacts'}`

function onBulkExited(_reason: string, result: BulkExitResult): void {
  showToast(
    result.exited
      ? `Exited ${contacts(result.exited)} from play` + (result.failed ? ` · ${result.failed} skipped` : '')
      : `Couldn’t exit ${contacts(result.failed)}`,
  )
  clearSelection()
  void load()
}

// The foreground-refetch floor is measured from the last finished load.
let lastLoadedAt = 0
watch(loading, (now, prev) => {
  if (prev && !now) lastLoadedAt = Date.now()
})


async function onBulkRemove(): Promise<void> {
  const contactIds = new Set<string>()
  for (const e of entries.value) if (selectedIds.value.has(e.contact_id)) contactIds.add(e.contact_id)
  if (!contactIds.size) return
  try {
    await removeFromDialList([...contactIds])
  } catch (e: any) {
    showToast(e?.message || 'Couldn’t remove them from the call list. Try again.')
    return
  }
  // People, not the response's entry count (a person with two lines counts twice there).
  showToast(`Removed ${contacts(contactIds.size)} from the call list`)
  clearSelection()
  void load()
}

// The rail has no live-update source (the AMD stream drops non-AMD events by
// design), so coming back to the tab refetches: debounced, never while a dialing
// run owns the queue snapshot, and never within 10 s of any finished load.
const MIN_FOREGROUND_REFETCH_MS = 10_000
let foregroundTimer: ReturnType<typeof setTimeout> | null = null
function refetchOnForeground(): void {
  if (sessionActive.value) return
  if (Date.now() - lastLoadedAt < MIN_FOREGROUND_REFETCH_MS) return
  if (foregroundTimer) clearTimeout(foregroundTimer)
  foregroundTimer = setTimeout(() => {
    foregroundTimer = null
    if (!sessionActive.value) void load()
  }, 200)
}
function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') refetchOnForeground()
}

const telephony = useTelephony()
function onOpenSettings(): void {
  telephony.openDrawer()
}

const onboarding = useDialerOnboarding()
const { me, isAdmin, isDialerOnly } = useCurrentUser()

// What the rep arrived to, read off the loaded queue and the ?selected rows.
function queueStateOnArrival(): QueueState {
  const linked = linkedContactIds(route.query.selected)
  if (linked.length > 1) {
    const named = new Set(linked)
    const found = railOrder.value.filter((e) => named.has(e.contact_id))
    if (found.length) {
      return {
        kind: 'people',
        count: found.length,
        companies: queuedCompanies(found),
        dial: { contactIds: found.map((e) => e.contact_id) },
      }
    }
  }
  const wanted = linked.length === 1 ? linked[0] : ''
  const row = wanted ? railOrder.value.find((e) => e.contact_id === wanted) : undefined
  const name = String(row?.contact_name || '').trim()
  if (row && name) {
    return {
      kind: 'person',
      contactId: row.contact_id,
      firstName: firstNameOf(name),
      name,
      detail: [row.account_name, row.contact_title].filter(Boolean).join(' · '),
      phone: row.prospect_e164,
      company: companyOf(row),
    }
  }
  if (entries.value.length > 0) {
    return { kind: 'people', count: entries.value.length, companies: queuedCompanies(railOrder.value), dial: 'queue' }
  }
  return { kind: 'empty' }
}

function companyOf(e: CallerQueueEntry): QueuedCompany | null {
  return e.account_domain ? { domain: e.account_domain, name: e.account_name } : null
}

// Each company once, in the order the rail will show their people.
function queuedCompanies(rows: CallerQueueEntry[]): QueuedCompany[] {
  const seen = new Set<string>()
  const companies: QueuedCompany[] = []
  for (const e of rows) {
    const company = companyOf(e)
    if (!company || seen.has(company.domain)) continue
    seen.add(company.domain)
    companies.push(company)
  }
  return companies
}

// The decision waits for the first queue read (an error reads as empty), so the
// intro can name what the rep came for. The reads behind it started at mount.
let onboardingDecided = false
watch(
  loaded,
  (isLoaded) => {
    if (!isLoaded || onboardingDecided) return
    onboardingDecided = true
    void onboarding.decide({ queueState: queueStateOnArrival(), isDialerOnly: isDialerOnly.value })
  },
  { immediate: true },
)

// Only the surfaces the drawer still holds. Team surfaces moved to
// /settings/phone.
const KNOWN_SURFACES: TelephonySurface[] = ['home', 'callback_own']
watch(
  () => route.query.settings,
  (val) => {
    if (!val) return
    const raw = String(val) as TelephonySurface
    telephony.openDrawer(KNOWN_SURFACES.includes(raw) ? raw : 'home')
    const nextQuery = { ...route.query }
    delete nextQuery.settings
    void router.replace({ path: route.path, query: nextQuery })
  },
  { immediate: true },
)

const session = useDialerSession()
const sessionActive = computed(() => session.phase.value !== 'idle')
const sessionControlsActive = computed(
  () =>
    session.mode.value === 'session' &&
    ['preparing', 'dialing', 'live', 'disposing'].includes(session.phase.value),
)
// A running session always owns the surface, verdict or not.
const showOnboarding = computed(() => onboarding.verdict.value === 'onboarding' && !sessionActive.value)
const undecided = computed(() => onboarding.verdict.value === 'undecided' && !sessionActive.value)

const emptyQueue = computed(
  () => loaded.value && !error.value && entries.value.length === 0 && !rowsFiltered.value,
)
const hideRail = computed(() => emptyQueue.value && !sessionActive.value)

// ── Adding contacts (dialer-only) ──
// The rail's foot carries it whenever the queue is idle; an empty queue has no rail, so
// the focus card's pane does. The flow has one source today: recent LeadIQ captures.
const canAddContacts = computed(
  () => isDialerOnly.value && loaded.value && !error.value && !sessionActive.value,
)
// Nobody can be power dialed: every row rests or is blocked, with no filter narrowing the view.
const nothingReady = computed(
  () => loaded.value && !error.value && visibleEntries.value.length === 0 && activeFilterCount.value === 0,
)
const queuedContactIds = computed(() => new Set(entries.value.map((e) => e.contact_id)))
const captures = useCapturedLeads({
  inQueue: (lead) => !!lead.contact_id && queuedContactIds.value.has(lead.contact_id),
})
const addOpen = ref(false)
// After an add lands server-side, the surface that offered it (the pick list, or the
// stepper's done page) stays up, disabled, until the refreshed queue is on screen behind
// it; the people just added lead that queue.
const landing = ref(false)
async function landAdded(): Promise<void> {
  landing.value = true
  try {
    await load({ resetSelection: true })
  } finally {
    landing.value = false
  }
}
function openAddContacts(): void {
  if (addOpen.value) return
  addOpen.value = true
  void captures.load()
}
function closeAddContacts(): void {
  addOpen.value = false
  captures.reset()
}
async function onAddCaptures(): Promise<void> {
  const results = await captures.add()
  if (!results) return
  const { added, already, skipped } = tallyOutcomes(results)
  await landAdded()
  closeAddContacts()
  showToast(
    (added ? `Added ${contacts(added)} to your call list` : `${contacts(already)} already in your queue`) +
      (added && already ? ` · ${already} already there` : '') +
      (skipped ? ` · ${skipped} couldn’t be added` : ''),
  )
}
// A call takes the surface; the pick list is not where a rep comes back to.
watch(sessionActive, (on) => {
  if (on) closeAddContacts()
})

// On-demand collapse on top of the width-driven one. Decided once, by the first
// populated read: one unfiltered person opens collapsed (nothing to pick from);
// a filtered read leaves it expanded so the filter stays reachable.
const railCollapsed = ref(false)
watch(
  () => entries.value.length,
  (n) => {
    railCollapsed.value = n === 1 && !rowsFiltered.value
  },
  { once: true },
)

const { setScope, clearScope } = useChatScope()
const { setContext, openDrawer, closeDrawer, drawerContext, drawerDismissedByUser } = useContextDrawer()

// Nothing parked in the drawer beside the setup.
watch(
  showOnboarding,
  (on) => {
    if (on) setContext(null)
  },
  { immediate: true },
)

// The drawer starts COLLAPSED, parked on Preferences, so opening the page
// never auto-expands on the first queue entry — the collapsed rail doubles as
// the settings affordance. A contact takes the slot only when a rep clicks a
// row. The ?settings deep-link (handled above) opens settings expanded instead.
// A ?selected= deep link is an explicit ask for a contact, so it takes the slot
// instead (handled once the first load lands, below).
if (!route.query.settings && !route.query.selected) {
  setContext({ type: DIALER_SETTINGS_CONTEXT_TYPE, surface: 'home' })
  closeDrawer()
}

// Whoever is being worked: the session's current contact while a session runs,
// otherwise the rail selection. Prefer a CONTACT context (mirrors the Contacts
// tab) so the ContactIdentityCard + contact panels show; fall back to account.
const drawerContact = computed(() => (sessionActive.value ? session.current.value : selected.value))

function applyContactContext(entry: CallerQueueEntry | null = drawerContact.value): void {
  if (entry?.contact_id && entry.account_id) {
    setContext({ type: DRAWER_CONTEXT_CONTACT, contactId: entry.contact_id, accountId: entry.account_id })
  } else if (entry?.account_id) {
    setContext({ type: DRAWER_CONTEXT_ACCOUNT, accountId: entry.account_id })
  }
}

function replaceSelectedQuery(contactId: string): void {
  const query = { ...route.query }
  if (contactId) query.selected = contactId
  else delete query.selected
  delete query.call
  void router.replace({ path: route.path, query })
}

async function scrollRowIntoView(entryId: string): Promise<void> {
  await nextTick()
  await nextTick()
  const el = document.querySelector(`[data-entry-id="${entryId}"]`)
  // Optional call: absent in jsdom.
  ;(el as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' })
}

// Follow the worked contact only once a contact/account context owns the slot:
// selection changes and session auto-advance keep it current, but never expand
// the drawer and never clobber the (parked or open) Preferences panel.
watch(drawerContact, () => {
  const t = drawerContext.value?.type
  if (t !== DRAWER_CONTEXT_CONTACT && t !== DRAWER_CONTEXT_ACCOUNT) return
  applyContactContext()
  // A filter change or a post-session reset moves the selection under an open
  // contact panel; the URL follows it.
  if (!sessionActive.value) replaceSelectedQuery(selected.value?.contact_id ?? '')
})

// A row click is the explicit ask for context: take the slot and expand — unless
// the rep closed the drawer themselves.
function onRailSelect(id: string): void {
  select(id)
  applyContactContext()
  if (!drawerDismissedByUser.value) openDrawer()
  replaceSelectedQuery(selected.value?.contact_id ?? '')
}

// The people named whom a run may dial on its own, in rail order: never anyone
// beyond them, so a selection can't widen into the whole queue.
function readyRunFor(contactIds: ReadonlySet<string>): CallerQueueEntry[] {
  return railOrder.value.filter((e) => contactIds.has(e.contact_id) && canAutoDial(e.dialability))
}
const selectionRun = computed(() => readyRunFor(selectedIds.value))

// Whether the running session was started over the checked people.
let runFromSelection = false
function startRun(run: CallerQueueEntry[], fromSelection = false): void {
  if (sessionActive.value || !run.length) return
  runFromSelection = fromSelection
  void session.start(run)
  applyContactContext(run[0])
}
// A run over the checked people uses them up once it has placed a call; one that
// never got that far (failed, or ended while connecting) keeps them for the retry.
watch(
  () => session.phase.value,
  (now) => {
    if (now === 'idle') runFromSelection = false
    else if (runFromSelection && (now === 'complete' || now === 'error_during') && session.stats.dialed > 0) {
      clearSelection()
    }
  },
)

// Arriving on a deep link. ?call=1 adds the "Call <first name>" cascade to the
// focus. Several ids arrive checked instead, and ?call=1 presses "Power dial
// selected". Both the queue and the onboarding decision must land first: a link
// that arrives mid-setup lands on the queue, never on the person.
const linkedIds = linkedContactIds(route.query.selected)
const deepLinkedContactId = linkedIds.length === 1 ? linkedIds[0] : ''
const deepLinkedCall = String(route.query.call ?? '').trim() === '1'
const deepLinkReady = computed(() => loaded.value && onboarding.verdict.value !== 'undecided')
if (linkedIds.length || deepLinkedCall) {
  // The linked row to reveal once the queue replaces the onboarding surface.
  let swapScrollId: string | null = null
  watch(showOnboarding, (on) => {
    if (on || !swapScrollId) return
    const id = swapScrollId
    swapScrollId = null
    void scrollRowIntoView(id)
  })
  const stopDeepLink = watch(
    deepLinkReady,
    (isReady) => {
      if (!isReady) return
      stopDeepLink()
      // A link is spent on arrival, session or not, so a refresh can never
      // re-fire it; a running session then owns the surface.
      if (linkedIds.length > 1) {
        const named = new Set(linkedIds)
        const found = railOrder.value.filter((e) => named.has(e.contact_id))
        const run = readyRunFor(named)
        const focus = (deepLinkedCall && run[0]) || found[0]
        // From now on ?selected is the focus again.
        replaceSelectedQuery(focus?.contact_id ?? '')
        if (sessionActive.value) return
        if (found.length) selectedIds.value = new Set(found.map((e) => e.contact_id))
        if (onboarding.shown.value) {
          if (!focus) return
          if (showOnboarding.value) swapScrollId = focus.id
          else void scrollRowIntoView(focus.id)
          return
        }
        if (!focus) {
          if (deepLinkedCall) showToast('Couldn’t find them in your queue')
          return
        }
        select(focus.id)
        applyContactContext(focus)
        openDrawer()
        void scrollRowIntoView(focus.id)
        if (!deepLinkedCall) return
        if (!run.length) {
          showToast('None of them can be called right now')
          return
        }
        startRun(run, true)
        const missing = linkedIds.length - found.length
        if (missing) showToast(`Couldn’t find ${missing} of them in your queue`)
        return
      }
      const row = railOrder.value.find((e) => e.contact_id === deepLinkedContactId)
      if (!row || deepLinkedCall) replaceSelectedQuery(row?.contact_id ?? '')
      if (sessionActive.value) return
      // Mid-setup: no toast over the step card, no dial; the done page's own
      // button is the call, and the finish handler parks the drawer.
      if (onboarding.shown.value) {
        if (!row) return
        if (showOnboarding.value) swapScrollId = row.id
        else void scrollRowIntoView(row.id)
        return
      }
      if (!row) {
        // Never dial the fallback row — that would call the wrong person.
        if (deepLinkedCall) showToast('Couldn’t find them in your queue')
        return
      }
      applyContactContext(row)
      openDrawer()
      void scrollRowIntoView(row.id)
      if (deepLinkedCall) {
        select(row.id)
        onCallContact()
      }
    },
    { immediate: true },
  )
}

// Three ways in, narrowing: the rail's CTA walks the checked people, or with
// nobody checked the whole visible queue; "Call <first name>" runs that same
// cascade over one contact, so their lines are tried in order until someone
// picks up; "One-off call" dials a single chosen line (one call → disposition →
// idle). Starting any of them is as explicit an ask for the contact's context as
// clicking their row: the drawer slot leaves a parked Preferences panel for the
// dialed contact (keeping the rep's expanded/collapsed choice), and the
// drawerContact watch follows from there as the session advances.
function onStartDialing(): void {
  if (selectedIds.value.size) startRun(selectionRun.value, true)
  else startRun(visibleEntries.value)
}

// The people just added are in the queue before the done page offers to dial them.
async function onAddSelected(): Promise<void> {
  await onboarding.addSelected()
  if (onboarding.stage.value === 'done') await landAdded()
}

// The done page's one button carries what the rep came for.
function onFinishOnboarding(): void {
  // Finish reads the queue, stale until the add lands; the disabled button isn't the only guard.
  if (landing.value) return
  const state = onboarding.queueState.value
  const added = onboarding.added.value
  onboarding.finish()
  setContext({ type: DIALER_SETTINGS_CONTEXT_TYPE, surface: 'home' })
  closeDrawer()
  if (state.kind === 'person') {
    // By the person: their row's id is its best line, which can change during setup.
    const row = railOrder.value.find((e) => e.contact_id === state.contactId)
    if (!row) {
      showToast('Couldn’t find them in your queue')
      return
    }
    select(row.id)
    onCallContact()
  } else if (state.kind === 'people' && state.dial !== 'queue') {
    // Only the people the link named, never the queue around them.
    const named = new Set(state.dial.contactIds)
    selectedIds.value = new Set(entries.value.filter((e) => named.has(e.contact_id)).map((e) => e.contact_id))
    const run = readyRunFor(named)
    if (!run.length) showToast('None of them can be called right now')
    startRun(run, true)
  } else if (state.kind === 'people' || added > 0) {
    // Through the rail's CTA, so anyone a link checked meanwhile scopes the run.
    onStartDialing()
  }
}
function onEndSession(): void {
  void session.endSession()
}
function onCallContact(): void {
  // Guard per contact so the disabled button isn't the only protection — the
  // backend /claim rejects a no-local-number line either way.
  if (sessionActive.value || !selected.value) return
  if (!canOneOffDial(selected.value.dialability)) return
  void session.start([selected.value])
  applyContactContext(selected.value)
}
function onOneOffCall(line: QueuedNumber): void {
  // No-local-number lines are never dialable (the backend /claim also rejects
  // them); guard per line so the disabled button isn't the only protection.
  if (sessionActive.value || !selected.value || !canOneOffDial(line.dialability)) return
  void session.start([], { oneOffEntry: selected.value, oneOffLine: line })
  applyContactContext(selected.value)
}
// Refetch the queue whenever a session OR a one-off call ends (phase returns to
// idle) so graduated / cooled-down contacts drop. (One-off completion resets
// straight to idle without an end-screen, so we can't rely on a button event.)
watch(
  () => session.phase.value,
  (now, prev) => {
    if (now !== 'idle' || !prev || prev === 'idle') return
    // A multi-contact session returns to the top of the refreshed queue; a one-off
    // stays on the contact the SDR chose (now 'cooldown' — unless it graduated out,
    // in which case load() falls back to the top automatically). `mode` survives
    // reset(), so it still reflects the run that just ended.
    void load({ resetSelection: session.mode.value !== 'oneoff' })
  },
)

// Guard against leaving mid-call (route change + tab close).
onBeforeRouteLeave(() => {
  const p = session.phase.value
  if (p === 'preparing') {
    // Nothing to confirm yet; left alone, the singleton would place the call off-page.
    void session.endSession()
    return true
  }
  if (p === 'dialing' || p === 'live' || p === 'disposing') {
    if (!window.confirm('Leave the live session? The current call will be hung up.')) return false
    void session.endSession()
  }
  return true
})
function onBeforeUnload(e: BeforeUnloadEvent): void {
  const p = session.phase.value
  if (p === 'dialing' || p === 'live' || p === 'disposing') {
    e.preventDefault()
    e.returnValue = ''
  }
}

onMounted(() => {
  setScope({ scopeType: 'human_dialer', scopeSubjectId: '', pageContext: null })
  void preloadCrmLinkConfig()
  void load()
  // Idempotent provisioning trigger + projection load (drives the pill / gear
  // badge / onboarding's phone step). Never throws to the page.
  void telephony.ensure()
  onboarding.init()
  // Held open for the whole session rather than per call: an answering-machine
  // verdict lands seconds into a call, and an SSE handshake racing that would
  // miss exactly the calls the feature exists for.
  openAmdStream()
  window.addEventListener('beforeunload', onBeforeUnload)
  window.addEventListener('focus', refetchOnForeground)
  document.addEventListener('visibilitychange', onVisibilityChange)
})
onUnmounted(() => {
  if (foregroundTimer) clearTimeout(foregroundTimer)
  window.removeEventListener('focus', refetchOnForeground)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  clearScope()
  // The dialer parks the shared drawer collapsed; hand the flag back open so
  // pages that set context without opening (accounts, history) keep behaving
  // as they did when the dialer used to leave the drawer expanded.
  openDrawer()
  telephony.stopAllPolling()
  closeAmdStream()
  window.removeEventListener('beforeunload', onBeforeUnload)
})
</script>

<style scoped>
.dialer {
  container: dialer / inline-size;
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background: var(--color-bg-app);
}
.dialerGrid {
  /* A third of the width, capped: past ~340px the rail's rows only gain empty
     space (names and roles ellipsize), so the call pane takes the rest. */
  --rail-cols: minmax(0, min(100% / 3, 340px)) minmax(0, 1fr);
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: var(--rail-cols);
  grid-template-rows: minmax(0, 1fr);
  overflow: hidden;
}
.dialerGrid.railCollapsed { --rail-cols: 50px minmax(0, 1fr); }
.dialerGrid.noRail { grid-template-columns: minmax(0, 1fr); }
/* overflow visible, not hidden: the rail's filter popover must escape the column
   (fixed is no escape — `.dialer` is a container, thus the containing block).
   Containment makes this a stacking context; z-index 2 keeps the popover above the call pane,
   whose session bar is z 1 (a pane overlay meant to cover the rail needs ≥ 3). */
.railWrap { container: rail / inline-size; position: relative; z-index: 2; min-width: 0; min-height: 0; display: flex; overflow: visible; }
/* Forced by width: the narrow column, and no toggle for what it cannot undo.
   Follows the base rules it overrides; .railWrap outranks IconButton's own display. */
@container dialer (max-width: 700px) {
  .dialerGrid { --rail-cols: 50px minmax(0, 1fr); }
  .railWrap .railToggle { display: none; }
}
.undecided { flex: 1; }

.statePanel {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--color-text-tertiary);
}
.stateTitle { font-family: var(--font-display); font-size: var(--fs-lg); color: var(--color-text-secondary); }
.stateText { font-size: var(--fs-sm); }
</style>
